import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import SEO from '../components/SEO';
import { vietnamProvinces } from '../utils/vietnamProvinces';

const getConditionLabel = (condition) => {
  const conditionMap = {
    'moi': 'Mới',
    'tot': 'Tốt',
    'binh-thuong': 'Bình thường',
    'cu': 'Cũ'
  };
  return conditionMap[condition] || condition;
};

const ItemDetail = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [requesting, setRequesting] = useState(false);
  
  // Chat state
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatContent, setChatContent] = useState('');
  const [loadingChat, setLoadingChat] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Related items state
  const [relatedItems, setRelatedItems] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);

  useEffect(() => {
    fetchItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (item) {
      // Add structured data for item
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Product",
        "name": item.title,
        "description": item.description || item.title,
        "image": item.image_url || undefined,
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "VND",
          "availability": item.status === 'available' ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
        },
        "category": item.category || "Khác"
      });
      script.id = 'item-structured-data';
      
      // Remove existing script if any
      const existing = document.getElementById('item-structured-data');
      if (existing) {
        existing.remove();
      }
      
      document.head.appendChild(script);
      
      return () => {
        const scriptToRemove = document.getElementById('item-structured-data');
        if (scriptToRemove) {
          scriptToRemove.remove();
        }
      };
    }
  }, [item]);

  useEffect(() => {
    if (user && item) {
      loadConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, item, id]);

  const fetchItem = async () => {
    try {
      const response = await axios.get(`/api/items/${id}`);
      setItem(response.data);
      // Fetch related items after item is loaded
      fetchRelatedItems();
    } catch (error) {
      console.error('Lỗi khi tải món đồ:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedItems = async () => {
    try {
      setLoadingRelated(true);
      const response = await axios.get(`/api/items/${id}/related`, {
        params: { limit: 6 }
      });
      setRelatedItems(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Lỗi khi tải món đồ liên quan:', error);
      setRelatedItems([]);
    } finally {
      setLoadingRelated(false);
    }
  };

  const handleRequest = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/login');
      return;
    }

    setError('');
    setSuccess('');
    setRequesting(true);

    try {
      await axios.post(`/api/items/${id}/request`, { message: '' });
      setSuccess('Gửi yêu cầu thành công! Chủ món đồ sẽ liên hệ với bạn.');
      // Reload item to get updated request status
      await fetchItem();
      // Reload conversation to show the new chat
      await loadConversation();
    } catch (error) {
      setError(error.response?.data?.error || 'Gửi yêu cầu thất bại');
    } finally {
      setRequesting(false);
    }
  };

  const loadConversation = async () => {
    if (!user || !item) return;
    
    try {
      setLoadingChat(true);
      const response = await axios.get(`/api/chat/item/${id}`);
      if (response.data.conversations && response.data.conversations.length > 0) {
        setConversations(response.data.conversations);
        // Auto-select first conversation if none selected or selected is not in list
        const firstConv = response.data.conversations[0];
        if (!selectedConversation || !response.data.conversations.find(c => c.id === selectedConversation.id)) {
          setSelectedConversation(firstConv);
          loadMessages(firstConv.id);
        } else {
          // Reload messages for currently selected conversation
          loadMessages(selectedConversation.id);
        }
      } else {
        setConversations([]);
        setSelectedConversation(null);
      }
    } catch (error) {
      // Conversation chưa tồn tại là bình thường
      if (error.response?.status !== 404) {
        console.error('Lỗi tải conversation:', error);
      }
      setConversations([]);
    } finally {
      setLoadingChat(false);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      const response = await axios.get(`/api/chat/messages/${conversationId}`);
      const msgs = Array.isArray(response.data) ? response.data : [];
      setMessages(msgs);
    } catch (error) {
      console.error('Lỗi tải tin nhắn:', error);
      setMessages([]);
    }
  };

  const startConversation = async () => {
    try {
      const { data: newConv } = await axios.post('/api/chat/start', { item_id: parseInt(id, 10) });
      setConversations([newConv]);
      setSelectedConversation(newConv);
      loadMessages(newConv.id);
    } catch (error) {
      console.error('Lỗi tạo conversation:', error);
      alert(error.response?.data?.error || 'Không thể tạo cuộc trò chuyện');
    }
  };

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    loadMessages(conv.id);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!chatContent.trim() || !selectedConversation || sendingMessage) return;

    try {
      setSendingMessage(true);
      await axios.post(`/api/chat/messages/${selectedConversation.id}`, {
        content: chatContent.trim()
      });
      // Reload messages to get sender info
      await loadMessages(selectedConversation.id);
      setChatContent('');
    } catch (error) {
      console.error('Lỗi gửi tin nhắn:', error);
      alert('Không thể gửi tin nhắn');
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ marginTop: '40px' }}>
        <div className="loading">Đang tải...</div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="container" style={{ marginTop: '40px' }}>
        <div className="error">Không tìm thấy món đồ</div>
      </div>
    );
  }

  const isOwner = user && user.id === item.user_id;
  const canRequest = user && !isOwner && item.status === 'available' && !item.user_request_status;
  const userRequestStatus = item.user_request_status; // 'pending', 'accepted', 'rejected'

  return (
    <>
      <SEO 
        title={item?.title}
        description={item?.description || `Xem chi tiết ${item?.title} trên NHO`}
        image={item?.image_url}
        type="article"
      />
      <div className="container" style={{ maxWidth: '900px', marginTop: '40px' }}>
        <div className="card">
          <div className="item-detail-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', marginBottom: '24px' }}>
          <div>
            {item.image_url ? (
              <img src={item.image_url} alt={item.title} style={{ width: '100%', borderRadius: '12px' }} />
            ) : (
              <div style={{ width: '100%', height: '400px', background: '#e0e0e0', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '64px' }}>
                📦
              </div>
            )}
          </div>
          <div>
            <h1 style={{ marginBottom: '16px' }}>{item.title}</h1>
            <div style={{ marginBottom: '16px' }}>
              <span className="badge badge-info" style={{ marginRight: '8px' }}>
                {item.category || 'Khác'}
              </span>
              <span className={`badge ${item.status === 'available' ? 'badge-success' : 'badge-warning'}`}>
                {item.status === 'available' ? 'Còn khả dụng' : 'Đã được nhận'}
              </span>
            </div>
            {item.description && (
              <div style={{ marginBottom: '24px' }}>
                <h3>Mô tả:</h3>
                <p style={{ color: '#666', lineHeight: '1.6' }}>{item.description}</p>
              </div>
            )}
            {item.condition && (
              <div style={{ marginBottom: '16px' }}>
                <strong>Tình trạng:</strong> {getConditionLabel(item.condition)}
              </div>
            )}
            {item.location && (
              <div style={{ marginBottom: '16px' }}>
                <strong>Vị trí:</strong> {vietnamProvinces.find(p => p.value === item.location)?.label || item.location}
              </div>
            )}
            <div style={{ marginBottom: '24px', padding: '16px', background: '#f5f5f5', borderRadius: '8px' }}>
              <h3 style={{ marginBottom: '8px' }}>Thông tin người cho:</h3>
              <p><strong>Tên:</strong> {item.owner_name || item.owner_username}</p>
              {item.owner_phone && <p><strong>Điện thoại:</strong> {item.owner_phone}</p>}
            </div>
          </div>
        </div>

        {canRequest && (
          <div className="request-section" style={{ marginTop: '32px', padding: '24px', background: '#f9f9f9', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <h3 style={{ marginBottom: '16px' }}>Xin món đồ này</h3>
            <form onSubmit={handleRequest}>
              {error && <div className="error" style={{ marginBottom: '12px' }}>{error}</div>}
              {success && <div className="success" style={{ marginBottom: '12px' }}>{success}</div>}
              <button type="submit" className="btn btn-primary" disabled={requesting}>
                {requesting ? 'Đang gửi...' : 'Gửi Yêu Cầu'}
              </button>
            </form>
          </div>
        )}

        {user && !isOwner && userRequestStatus && (
          <div className="request-status-section" style={{ marginTop: '32px', padding: '24px', background: '#f9f9f9', borderRadius: '8px' }}>
            {userRequestStatus === 'pending' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span className="badge badge-warning" style={{ fontSize: '14px', padding: '8px 16px' }}>
                  Đang chờ phản hồi
                </span>
                <p style={{ margin: 0, color: '#666' }}>Yêu cầu của bạn đang được xem xét</p>
              </div>
            )}
            {userRequestStatus === 'accepted' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span className="badge badge-success" style={{ fontSize: '14px', padding: '8px 16px' }}>
                  Đã được chấp nhận
                </span>
                <p style={{ margin: 0, color: '#2e7d32' }}>Yêu cầu của bạn đã được chấp nhận!</p>
              </div>
            )}
            {userRequestStatus === 'rejected' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <span className="badge badge-danger" style={{ fontSize: '14px', padding: '8px 16px', fontWeight: '700' }}>
                    Đã bị từ chối
                  </span>
                </div>
                <div style={{ 
                  padding: '12px 16px', 
                  background: '#ffebee', 
                  borderRadius: '6px',
                  border: '1px solid #ffcdd2'
                }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#c62828', fontWeight: '500' }}>
                    ⚠️ Yêu cầu của bạn đã bị từ chối
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Chat Box - Hiển thị cho cả owner và requester */}
        {user && (isOwner || canRequest || conversations.length > 0) && (
          <div style={{ marginTop: '32px', padding: '24px', background: '#f9f9f9', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '16px' }}>
              {isOwner ? 'Chat với người xin món đồ' : 'Chat với chủ món đồ'}
            </h3>
            
            {conversations.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <p style={{ marginBottom: '16px', color: '#666' }}>
                  {isOwner 
                    ? 'Chưa có cuộc trò chuyện nào cho món đồ này'
                    : 'Bắt đầu trò chuyện với chủ món đồ'}
                </p>
                {!isOwner && (
                  <button onClick={startConversation} className="btn btn-primary">
                    Bắt đầu chat
                  </button>
                )}
              </div>
            ) : (
              <div className="item-detail-chat-container" style={{ display: 'flex', gap: '16px', minHeight: '500px' }}>
                {/* Danh sách conversations cho chủ món đồ */}
                {isOwner && conversations.length > 1 && (
                  <div className="item-detail-chat-sidebar" style={{ 
                    width: '220px', 
                    flexShrink: 0,
                    borderRight: '1px solid #ddd', 
                    paddingRight: '12px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <h4 style={{ fontSize: '14px', marginBottom: '12px', color: '#666', fontWeight: '600' }}>Người xin:</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto', flex: 1 }}>
                      {conversations.map((conv) => {
                        const requesterName = conv.requester?.full_name || conv.requester?.username || `User #${conv.requester_id}`;
                        const isSelected = selectedConversation?.id === conv.id;
                        return (
                          <button
                            key={conv.id}
                            onClick={() => handleSelectConversation(conv)}
                            className="btn btn-outline"
                            style={{
                              padding: '10px 12px',
                              fontSize: '13px',
                              background: isSelected ? '#7b1fa2' : '#f5f5f5',
                              borderColor: isSelected ? '#7b1fa2' : '#ddd',
                              color: isSelected ? '#fff' : '#333',
                              textAlign: 'left',
                              fontWeight: isSelected ? '600' : '400',
                              transition: 'all 0.2s',
                              boxShadow: isSelected ? '0 2px 4px rgba(123, 31, 162, 0.2)' : 'none'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) {
                                e.target.style.background = '#e8e8e8';
                                e.target.style.borderColor = '#bbb';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) {
                                e.target.style.background = '#f5f5f5';
                                e.target.style.borderColor = '#ddd';
                              }
                            }}
                          >
                            {requesterName}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Chat messages và form - luôn nằm cùng nhau */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  {selectedConversation ? (
                    <>
                      {isOwner && conversations.length > 1 && (
                        <div style={{ marginBottom: '12px', padding: '10px 12px', background: '#fff', borderRadius: '8px', fontSize: '14px', border: '1px solid #ddd' }}>
                          <strong>Đang chat với:</strong> {selectedConversation.requester?.full_name || selectedConversation.requester?.username || `User #${selectedConversation.requester_id}`}
                        </div>
                      )}
                      <div
                        style={{
                          flex: 1,
                          minHeight: '300px',
                          maxHeight: '450px',
                          overflowY: 'auto',
                          padding: '16px',
                          background: '#fff',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          marginBottom: '12px'
                        }}
                      >
                        {loadingChat ? (
                          <div className="loading">Đang tải...</div>
                        ) : messages.length === 0 ? (
                          <div className="empty-state" style={{ padding: '12px 0' }}>
                            Chưa có tin nhắn nào. Hãy bắt đầu trò chuyện!
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {messages.map((m) => {
                              const senderName = m.sender?.full_name || m.sender?.username || `User #${m.sender_id}`;
                              const isMe = m.sender_id === user.id;
                              return (
                                <div
                                  key={m.id}
                                  style={{
                                    alignSelf: isMe ? 'flex-end' : 'flex-start',
                                    background: isMe ? '#e1bee7' : '#fff',
                                    color: '#4a148c',
                                    padding: '10px 12px',
                                    borderRadius: '12px',
                                    maxWidth: '70%',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                    border: isMe ? 'none' : '1px solid #ddd'
                                  }}
                                >
                                  {!isMe && (
                                    <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#7b1fa2' }}>
                                      {senderName}
                                    </div>
                                  )}
                                  <div style={{ fontSize: '14px', lineHeight: 1.5 }}>{m.content}</div>
                                  <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                                    {new Date(m.created_at).toLocaleString('vi-VN')}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
                        <textarea
                          value={chatContent}
                          onChange={(e) => setChatContent(e.target.value)}
                          placeholder="Nhập tin nhắn..."
                          rows="2"
                          style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px' }}
                        />
                        <button 
                          type="submit" 
                          className="btn btn-primary" 
                          disabled={sendingMessage || !chatContent.trim()}
                          style={{ alignSelf: 'flex-end', padding: '12px 16px' }}
                        >
                          {sendingMessage ? 'Đang gửi...' : 'Gửi'}
                        </button>
                      </form>
                    </>
                  ) : (
                    <div className="empty-state" style={{ padding: '20px', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      Chọn một cuộc trò chuyện để xem tin nhắn
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {isOwner && (
          <div style={{ marginTop: '24px' }}>
            <p style={{ color: '#666' }}>Đây là món đồ của bạn</p>
            <button onClick={() => navigate('/my-items')} className="btn btn-secondary">
              Quản lý món đồ của tôi
            </button>
          </div>
        )}

        {!user && item.status === 'available' && (
          <div style={{ marginTop: '24px', padding: '16px', background: '#fff3cd', borderRadius: '8px', textAlign: 'center' }}>
            <p>Bạn cần đăng nhập để xin món đồ này</p>
            <button onClick={() => navigate('/login')} className="btn btn-primary" style={{ marginTop: '12px' }}>
              Đăng Nhập
            </button>
          </div>
        )}

        {/* Related Items Section */}
        {relatedItems.length > 0 && (
          <div style={{ marginTop: '48px', paddingTop: '32px', borderTop: '2px solid #e0e0e0' }}>
            <h2 style={{ marginBottom: '24px', fontSize: '24px', fontWeight: '600', color: '#333' }}>
              Món đồ liên quan
            </h2>
            {loadingRelated ? (
              <div className="loading">Đang tải...</div>
            ) : (
              <div className="item-list">
                {relatedItems.map(relatedItem => (
                  <article
                    key={relatedItem.id}
                    className="item-list-item"
                    onClick={() => navigate(`/item/${relatedItem.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    {relatedItem.image_url ? (
                      <img 
                        src={relatedItem.image_url} 
                        alt={relatedItem.title} 
                        className="item-list-image"
                        loading="lazy"
                      />
                    ) : (
                      <div className="item-list-image placeholder" aria-label="Không có hình ảnh">📦</div>
                    )}
                    <div className="item-list-content">
                      <h2 className="item-list-title">{relatedItem.title}</h2>
                      <p className="item-list-description">{relatedItem.description || 'Không có mô tả'}</p>
                      <div className="item-list-meta">
                        <span className="badge badge-info">{relatedItem.category || 'Khác'}</span>
                        {relatedItem.location && (
                          <span className="badge badge-secondary" style={{ fontSize: '11px' }}>
                            {vietnamProvinces.find(p => p.value === relatedItem.location)?.label || relatedItem.location}
                          </span>
                        )}
                        <span className="item-list-owner">{relatedItem.owner_name || relatedItem.owner_username}</span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default ItemDetail;

